import React from 'react';
import { Nav, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { prefetchRoute } from '../../../routes/routePrefetch';

/**
 * Sidebar menu building blocks (MenuItem, SubMenuItem, ActionMenuItem).
 * Factory keeps NavigationBar lean while preserving closure over sidebar state.
 */
export function createSidebarMenuComponents({
  collapsed,
  expandedMenus,
  activeMenuId,
  toggleMenu,
  setCollapsed,
  setExpandedMenus,
  location,
  closeSidebarFlyouts,
}) {
  const MenuItem = ({ icon, customIcon, title, id, children }) => {
    const isOpen = expandedMenus[id];
    const isActive = activeMenuId === id;

    const content = (
      <Nav.Link
        className={`d-flex align-items-center px-3 py-2 sidebar-menu-item ${isOpen ? 'bg-secondary bg-opacity-25' : ''} ${isActive ? 'sidebar-menu-active' : ''}`}
        onClick={() => {
          closeSidebarFlyouts?.();
          toggleMenu(id);
        }}
        style={{
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background-color 0.3s ease, color 0.3s ease',
        }}
      >
        {customIcon ? (
          <div className="sidebar-menu-icon" style={{ minWidth: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {customIcon}
          </div>
        ) : (
          <i className={`bi ${icon} sidebar-menu-icon`} style={{ fontSize: '1.2rem', minWidth: '24px' }} />
        )}
        {!collapsed && (
          <>
            <span className="ms-3 flex-grow-1">{title}</span>
            <i className={`bi bi-chevron-${isOpen ? 'down' : 'right'} ms-auto`} style={{ fontSize: '0.8rem' }} />
          </>
        )}
      </Nav.Link>
    );

    return (
      <>
        {collapsed ? (
          <OverlayTrigger
            placement="right"
            overlay={<Tooltip>{title}</Tooltip>}
            container={document.body}
            popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }] }}
          >
            {content}
          </OverlayTrigger>
        ) : content}
        <Collapse in={!collapsed && isOpen} timeout={300}>
          <div style={{ transition: 'all 0.3s ease-in-out' }}>
            <div className="bg-dark bg-opacity-50">{children}</div>
          </div>
        </Collapse>
      </>
    );
  };

  const SubMenuItem = ({ to, children, onClick, indentClass = 'ps-5' }) => {
    let linkTo = to || '#';
    if (typeof linkTo === 'string' && linkTo.includes('?')) {
      const queryIndex = linkTo.indexOf('?');
      linkTo = { pathname: linkTo.slice(0, queryIndex), search: linkTo.slice(queryIndex) };
    }

    const targetPathname = typeof linkTo === 'object' ? linkTo.pathname : linkTo;
    const targetSearch = typeof linkTo === 'object' ? (linkTo.search || '') : '';
    const isSubActive = location.pathname === targetPathname
      && (!targetSearch || location.search === targetSearch);

    const handleClick = (e) => {
      if (window.innerWidth <= 991.98) {
        setCollapsed(true);
        setExpandedMenus({});
      }
      if (onClick) onClick(e);
    };

    const handlePrefetch = () => prefetchRoute(targetPathname);

    return (
      <LinkContainer to={linkTo}>
        <Nav.Link
          className={`${indentClass} py-1 d-block text-truncate sidebar-submenu-item ${isSubActive ? 'sidebar-submenu-active' : 'text-white-50'}`}
          onClick={handleClick}
          onMouseEnter={handlePrefetch}
          onFocus={handlePrefetch}
          onTouchStart={handlePrefetch}
        >
          {children}
        </Nav.Link>
      </LinkContainer>
    );
  };

  const SubMenuGroup = ({
    id,
    title,
    children,
    isActive = false,
    indentClass = 'ps-5',
    parentId,
  }) => {
    const isOpen = expandedMenus[id];
    const keepOpenIds = parentId ? [parentId] : [];

    const handleToggle = (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeSidebarFlyouts?.();
      toggleMenu(id, { keepOpenIds });
    };

    return (
      <>
        <Nav.Link
          className={`${indentClass} py-1 d-flex align-items-center sidebar-submenu-item ${isOpen ? 'bg-secondary bg-opacity-10' : ''} ${isActive ? 'sidebar-submenu-active' : 'text-white-50'}`}
          style={{ cursor: 'pointer' }}
          onClick={handleToggle}
          aria-expanded={isOpen}
        >
          <span className="flex-grow-1">{title}</span>
          {!collapsed && (
            <i className={`bi bi-chevron-${isOpen ? 'down' : 'right'} sidebar-submenu-chevron`} />
          )}
        </Nav.Link>
        <Collapse in={!collapsed && isOpen} timeout={300}>
          <div className="sidebar-submenu-group-panel">{children}</div>
        </Collapse>
      </>
    );
  };

  const ActionMenuItem = ({ onClick, children }) => {
    const handleClick = (e) => {
      if (window.innerWidth <= 991.98) {
        setCollapsed(true);
        setExpandedMenus({});
      }
      if (onClick) onClick(e);
    };

    return (
      <Nav.Link
        className="text-white-50 ps-5 py-1 d-block text-truncate sidebar-submenu-item"
        style={{ cursor: 'pointer' }}
        onClick={handleClick}
      >
        {children}
      </Nav.Link>
    );
  };

  /**
   * A top-level row that performs an action instead of opening a submenu.
   *
   * `ActionMenuItem` is indented submenu chrome; this one wears `MenuItem`'s
   * clothes (icon, label, collapsed tooltip) but has no chevron and no
   * children. Used for "leave this workspace", which has to read as a
   * first-class exit rather than as an item buried inside a menu.
   */
  const TopLevelActionItem = ({ icon, customIcon, title, onClick }) => {
    const handleClick = () => {
      closeSidebarFlyouts?.();
      if (window.innerWidth <= 991.98) {
        setCollapsed(true);
        setExpandedMenus({});
      }
      if (onClick) onClick();
    };

    const content = (
      <Nav.Link
        className="d-flex align-items-center px-3 py-2 sidebar-menu-item"
        onClick={handleClick}
        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
      >
        {customIcon ? (
          <div
            className="sidebar-menu-icon"
            style={{ minWidth: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {customIcon}
          </div>
        ) : (
          <i className={`bi ${icon} sidebar-menu-icon`} style={{ fontSize: '1.2rem', minWidth: '24px' }} />
        )}
        {!collapsed && <span className="ms-3 flex-grow-1">{title}</span>}
      </Nav.Link>
    );

    return collapsed ? (
      <OverlayTrigger
        placement="right"
        overlay={<Tooltip>{title}</Tooltip>}
        container={document.body}
        popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }] }}
      >
        {content}
      </OverlayTrigger>
    ) : content;
  };

  const renderCollapsedSidebarAction = (actionId, { onClick, tooltip, ariaLabel, icon }) => {
    const button = (
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        className="d-flex align-items-center justify-content-center"
        style={{ cursor: 'pointer', color: '#B0B0B0', width: '26px', height: '26px' }}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
      >
        {icon}
      </div>
    );

    return (
      <OverlayTrigger
        key={actionId}
        placement="right"
        overlay={<Tooltip id={`tt-sidebar-${actionId}`}>{tooltip}</Tooltip>}
        container={document.body}
        popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }] }}
      >
        {button}
      </OverlayTrigger>
    );
  };

  const closeMobileSidebar = () => {
    if (window.innerWidth <= 991.98) {
      setCollapsed(true);
      setExpandedMenus({});
    }
  };

  return {
    MenuItem,
    SubMenuItem,
    SubMenuGroup,
    ActionMenuItem,
    TopLevelActionItem,
    renderCollapsedSidebarAction,
    closeMobileSidebar,
  };
}
